import { type LineItem, type OrderPricingMetadata } from '@alto/scriptdash/alto/pricing/patients/v3/pricing_endpoint';
import type { MinimalShipmentFee } from '@alto/scriptdash/alto/scheduling/types/v1/minimal_shipment_fee';
import { useQuery } from '@tanstack/react-query';
import { keyBy } from 'lodash';
import { useMemo } from 'react';
import { staleTimes } from './constants';
import { queries } from './query-keys';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { getIsOrderPayAtPickup, getIsOrderPickupMethod } from '~shared/features/checkout/selectors/getOrder';
import { getFetchPricingOrderParam } from '~shared/features/pricing/selectors/getFetchPricingOrderParam';
import { type ItemPricing } from '~shared/features/pricing/types';
import getIsAuthorized from '~shared/selectors/auth/getIsAuthorized';
import { useSelectorShared } from '~shared/store';

export type OrderPricingQueryData = {
  has_copay: boolean;
  item_pricing: ItemPricing;
  line_items: LineItem[];
  metadata: OrderPricingMetadata;
  price_unconfirmed: boolean;
  subtotal: number | null;
  total: LineItem | undefined;
};

export type OrderPricing = {
  appliedShipmentFees: MinimalShipmentFee[];
  hasAppliedDeliveryFee: boolean;
  isError: boolean;
  isOrderPricingLoading: boolean;
  isPaymentMethodRequired: boolean;
  isSuccess: boolean;
  lastFetchedAt: number;
  orderPricing: OrderPricingQueryData | undefined;
};

type Props = {
  shipmentID?: number;
  bundlingOptionShipmentID?: number;
};

export const useOrderPricing = ({ shipmentID, bundlingOptionShipmentID }: Props = {}) => {
  const { cartItems, selectedPaymentTypes } = useCartData();
  const isAuthorized = useSelectorShared(getIsAuthorized);
  const isOrderPickupMethod = useSelectorShared(getIsOrderPickupMethod);
  const isOrderPayAtPickup = useSelectorShared(getIsOrderPayAtPickup);
  const pricingOrder = useSelectorShared((state) =>
    getFetchPricingOrderParam(state, { cartItems, selectedPaymentTypes }),
  );
  const { data, isError, isPending, isRefetching, isSuccess, refetch } = useQuery({
    ...queries.pricing.order({
      shipmentID: shipmentID ? shipmentID : undefined,
      order: shipmentID ? undefined : pricingOrder,
      bundlingOptionShipmentID,
    }),
    enabled: isAuthorized,
    staleTime: staleTimes.fiveMinutes,
  });

  /**
   * Determine if a payment method is required for medications only.
   * If the price is unavailable (i.e. billing issue) or the subtotal
   * is zero, then return false. Otherwise, return true.
   */
  const isPaymentMethodRequired = useMemo(() => {
    if (isOrderPickupMethod && isOrderPayAtPickup) {
      return false;
    }

    if (!data?.total) {
      return true;
    }

    return (data?.subtotal ?? 0) > 0 || data?.total.info_key === 'price_unavailable';
  }, [isOrderPickupMethod, isOrderPayAtPickup, data?.total, data?.subtotal]);

  const appliedShipmentFees = useMemo(() => {
    const appliedFees: MinimalShipmentFee[] = [];
    if (!data) {
      return appliedFees;
    }

    const { line_items } = data;
    for (const lineItem of line_items) {
      const { key, value } = lineItem;

      if (key === 'essentials_only_fee') {
        appliedFees.push({
          fee_type: 'essentials_only_delivery',
          amount: value?.toString() || '',
        });
      } else if (key === 'same_day_delivery_fee') {
        appliedFees.push({
          fee_type: 'same_day_delivery',
          amount: value?.toString() || '',
        });
      } else if (key === 'next_day_delivery_fee') {
        appliedFees.push({
          fee_type: 'next_day_delivery',
          amount: value?.toString() || '',
        });
      } else if (key === 'standard_delivery_fee') {
        appliedFees.push({
          fee_type: 'standard_delivery',
          amount: value?.toString() || '',
        });
      } else if (key === 'on_demand_fee') {
        appliedFees.push({
          fee_type: 'on_demand_delivery',
          amount: value?.toString() || '',
        });
      } else if (key === 'asap_delivery_fee') {
        appliedFees.push({
          fee_type: 'asap_delivery',
          amount: value?.toString() || '',
        });
      }
    }

    return appliedFees;
  }, [data]);

  const appliedDeliveryFee = useMemo(() => {
    const appliedFeesByType = keyBy(appliedShipmentFees, 'fee_type');

    const sameDayDeliveryFee = appliedFeesByType['same_day_delivery'];
    const nextDayDeliveryFee = appliedFeesByType['next_day_delivery'];
    const standardDeliveryFee = appliedFeesByType['standard_delivery'];
    const asapDeliveryFee = appliedFeesByType['asap_delivery'];

    return sameDayDeliveryFee || nextDayDeliveryFee || standardDeliveryFee || asapDeliveryFee;
  }, [appliedShipmentFees]);

  return {
    appliedShipmentFees,
    hasAppliedDeliveryFee: !!appliedDeliveryFee && parseFloat(appliedDeliveryFee.amount) > 0,
    isError,
    isOrderPricingLoading: isPending || isRefetching,
    isPaymentMethodRequired,
    isSuccess,
    lastFetchedAt: Date.now(),
    orderPricing: data,
    refetchOrderPricing: refetch,
  };
};
